import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  getAnalyticsData
} from '../../utils/visitorTracking';
import './VisitorChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const VisitorChart = () => {
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [uniqueVisitors, setUniqueVisitors] = useState(0);
  const [routes, setRoutes] = useState([]);
  const [visitsByDate, setVisitsByDate] = useState({});
  const [chartType, setChartType] = useState('bar'); // 'bar', 'line', or 'doughnut'

  useEffect(() => {
    loadData();
    // Refresh data every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const analyticsData = await getAnalyticsData();
      
      setTotalVisitors(analyticsData.totalVisitors || 0);
      setUniqueVisitors(analyticsData.uniqueVisitors || 0);
      
      const routesData = analyticsData.routesVisited || [];
      setRoutes(routesData.slice(0, 10)); // Top 10 routes
      
      // Group visits by date
      const allVisits = analyticsData.allVisits || [];
      const dateGroups = {};
      allVisits.forEach(visit => {
        const date = visit.date || new Date(visit.timestamp).toLocaleDateString();
        dateGroups[date] = (dateGroups[date] || 0) + 1;
      });
      
      // Sort dates and get last 30 days
      const sortedDates = Object.keys(dateGroups).sort((a, b) => {
        return new Date(a) - new Date(b);
      }).slice(-30);
      
      const visitsByDateData = {};
      sortedDates.forEach(date => {
        visitsByDateData[date] = dateGroups[date];
      });
      
      setVisitsByDate(visitsByDateData);
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  // Routes chart data
  const routesChartData = {
    labels: routes.map(r => r.route === '/' ? 'Home' : r.route.replace('/', '')),
    datasets: [
      {
        label: 'Page Visits',
        data: routes.map(r => r.count),
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(118, 75, 162, 0.8)',
          'rgba(15, 189, 231, 0.8)',
          'rgba(50, 205, 50, 0.8)',
          'rgba(245, 121, 25, 0.8)',
          'rgba(220, 38, 38, 0.8)',
          'rgba(37, 60, 246, 0.8)',
          'rgba(199, 115, 221, 0.8)',
          'rgba(255, 107, 53, 0.8)',
          'rgba(139, 69, 19, 0.8)',
        ],
        borderColor: [
          'rgba(102, 126, 234, 1)',
          'rgba(118, 75, 162, 1)',
          'rgba(15, 189, 231, 1)',
          'rgba(50, 205, 50, 1)',
          'rgba(245, 121, 25, 1)',
          'rgba(220, 38, 38, 1)',
          'rgba(37, 60, 246, 1)',
          'rgba(199, 115, 221, 1)',
          'rgba(255, 107, 53, 1)',
          'rgba(139, 69, 19, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  // Visits over time chart data
  const visitsOverTimeData = {
    labels: Object.keys(visitsByDate),
    datasets: [
      {
        label: 'Visits per Day',
        data: Object.values(visitsByDate),
        backgroundColor: 'rgba(102, 126, 234, 0.2)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Doughnut chart data (routes)
  const doughnutChartData = {
    labels: routes.map(r => r.route === '/' ? 'Home' : r.route.replace('/', '')),
    datasets: [
      {
        data: routes.map(r => r.count),
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(118, 75, 162, 0.8)',
          'rgba(15, 189, 231, 0.8)',
          'rgba(50, 205, 50, 0.8)',
          'rgba(245, 121, 25, 0.8)',
          'rgba(220, 38, 38, 0.8)',
          'rgba(37, 60, 246, 0.8)',
          'rgba(199, 115, 221, 0.8)',
          'rgba(255, 107, 53, 0.8)',
          'rgba(139, 69, 19, 0.8)',
        ],
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Page Visits by Route',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: chartType === 'doughnut' ? {} : {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Visits Over Time (Last 30 Days)',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div className="visitor-chart-wrapper">
      <div className="chart-header">
        <h3>Visitor Statistics</h3>
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-label">Total Visits</span>
            <span className="stat-value">{totalVisitors}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Unique Visitors</span>
            <span className="stat-value">{uniqueVisitors}</span>
          </div>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-section">
          <div className="chart-controls">
            <h4>Page Visits by Route</h4>
            <div className="chart-type-buttons">
              <button
                className={chartType === 'bar' ? 'active' : ''}
                onClick={() => setChartType('bar')}
              >
                Bar
              </button>
              <button
                className={chartType === 'line' ? 'active' : ''}
                onClick={() => setChartType('line')}
              >
                Line
              </button>
              <button
                className={chartType === 'doughnut' ? 'active' : ''}
                onClick={() => setChartType('doughnut')}
              >
                Doughnut
              </button>
            </div>
          </div>
          <div className="chart-container">
            {chartType === 'bar' && <Bar data={routesChartData} options={chartOptions} />}
            {chartType === 'line' && <Line data={routesChartData} options={chartOptions} />}
            {chartType === 'doughnut' && <Doughnut data={doughnutChartData} options={chartOptions} />}
          </div>
        </div>

        <div className="chart-section">
          <h4>Visits Over Time</h4>
          <div className="chart-container">
            <Line data={visitsOverTimeData} options={lineChartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorChart;

